import React, { useState } from 'react';
import { Palette, Check, Sparkles } from 'lucide-react';

const ColorCatalog = () => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedFinish, setSelectedFinish] = useState('matte');

  const colorCategories = [
    {
      name: 'צבעים חמים',
      colors: [
        { id: 1, name: 'בז׳ חם', hex: '#D4A574', rgb: 'rgb(212, 165, 116)' },
        { id: 2, name: 'טרקוטה', hex: '#E07856', rgb: 'rgb(224, 120, 86)' },
        { id: 3, name: 'חרדל רך', hex: '#D4A259', rgb: 'rgb(212, 162, 89)' },
        { id: 4, name: 'אפרסק', hex: '#FFCBA4', rgb: 'rgb(255, 203, 164)' },
      ]
    },
    {
      name: 'צבעים קרים',
      colors: [
        { id: 5, name: 'כחול ים', hex: '#4A90A4', rgb: 'rgb(74, 144, 164)' },
        { id: 6, name: 'ירוק מנטה', hex: '#98D8C8', rgb: 'rgb(152, 216, 200)' },
        { id: 7, name: 'אפור כחלחל', hex: '#6B7F8C', rgb: 'rgb(107, 127, 140)' },
        { id: 8, name: 'לבנדר', hex: '#C7CEEA', rgb: 'rgb(199, 206, 234)' },
      ]
    },
    {
      name: 'צבעים ניטרליים',
      colors: [
        { id: 9, name: 'אפור חם', hex: '#9D9D9D', rgb: 'rgb(157, 157, 157)' },
        { id: 10, name: 'קרם', hex: '#F5E6D3', rgb: 'rgb(245, 230, 211)' },
        { id: 11, name: 'שנהב', hex: '#FFFFF0', rgb: 'rgb(255, 255, 240)' },
        { id: 12, name: 'שחור פחם', hex: '#36454F', rgb: 'rgb(54, 69, 79)' },
      ]
    },
    {
      name: 'צבעים נועזים',
      colors: [
        { id: 13, name: 'אדום יין', hex: '#722F37', rgb: 'rgb(114, 47, 55)' },
        { id: 14, name: 'ירוק יער', hex: '#2D5016', rgb: 'rgb(45, 80, 22)' },
        { id: 15, name: 'כחול נייבי', hex: '#000080', rgb: 'rgb(0, 0, 128)' },
        { id: 16, name: 'זהב עתיק', hex: '#B8860B', rgb: 'rgb(184, 134, 11)' },
      ]
    }
  ];

  const finishTypes = [
    { id: 'matte', name: 'מאט', description: 'גימור אלגנטי ללא ברק' },
    { id: 'satin', name: 'סאטן', description: 'ברק עדין ומתון' },
    { id: 'glossy', name: 'מבריק', description: 'גימור מבריק ומרשים' },
    { id: 'metallic', name: 'מטאלי', description: 'אפקט מתכתי ייחודי' },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-white via-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-amber-100 p-4 rounded-2xl">
              <Palette className="w-12 h-12 text-amber-600" />
            </div>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-amber-900 mb-6">
            קטלוג צבעים
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-8 rounded-full"></div>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
            בחרו מתוך מגוון רחב של צבעים וגימורים איכוטיים
          </p>
        </div>

        {/* Finish Selection */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
            בחרו סוג גימור
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {finishTypes.map((finish) => (
              <button
                key={finish.id}
                onClick={() => setSelectedFinish(finish.id)}
                className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                  selectedFinish === finish.id
                    ? 'border-amber-500 bg-amber-50 shadow-xl scale-105'
                    : 'border-gray-200 bg-white hover:border-amber-300 hover:shadow-lg'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-lg text-gray-800">
                    {finish.name}
                  </span>
                  {selectedFinish === finish.id && (
                    <Check className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600">{finish.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Color Categories */}
        <div className="space-y-12">
          {colorCategories.map((category) => (
            <div key={category.name}>
              <h3 className="text-2xl font-bold text-amber-900 mb-6 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-amber-600" />
                {category.name}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {category.colors.map((color) => (
                  <div
                    key={color.id}
                    onClick={() => setSelectedColor(color)}
                    className={`group cursor-pointer bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                      selectedColor?.id === color.id
                        ? 'ring-4 ring-amber-500 scale-105'
                        : 'hover:scale-105'
                    }`}
                  >
                    {/* Color Preview */}
                    <div className="relative aspect-square overflow-hidden">
                      <div
                        className="w-full h-full transition-transform duration-300 group-hover:scale-110"
                        style={{
                          background: color.hex,
                          boxShadow: selectedFinish === 'glossy'
                            ? 'inset 0 0 60px rgba(255,255,255,0.3)'
                            : selectedFinish === 'metallic'
                            ? `linear-gradient(135deg, ${color.hex} 0%, rgba(255,255,255,0.4) 50%, ${color.hex} 100%)`
                            : 'none'
                        }}
                      >
                        {selectedFinish === 'metallic' && (
                          <div
                            className="absolute inset-0"
                            style={{
                              background: `linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)`,
                            }}
                          />
                        )}
                      </div>

                      {/* Checkmark for selected */}
                      {selectedColor?.id === color.id && (
                        <div className="absolute top-3 right-3 bg-amber-500 rounded-full p-2">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Color Info */}
                    <div className="p-4">
                      <h4 className="font-bold text-lg text-gray-800 mb-2">
                        {color.name}
                      </h4>
                      <div className="space-y-1 text-xs text-gray-600">
                        <p className="font-mono">{color.hex}</p>
                        <p className="font-mono">{color.rgb}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Selected Color Display */}
        {selectedColor && (
          <div className="mt-16 bg-white rounded-2xl shadow-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
              הצבע שבחרתם
            </h3>
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div
                className="aspect-square rounded-2xl shadow-xl"
                style={{
                  background: selectedColor.hex,
                  boxShadow: selectedFinish === 'glossy'
                    ? 'inset 0 0 80px rgba(255,255,255,0.4)'
                    : 'none'
                }}
              />
              <div className="space-y-4">
                <div>
                  <h4 className="text-3xl font-bold text-gray-800 mb-2">
                    {selectedColor.name}
                  </h4>
                  <p className="text-amber-600 font-semibold">
                    גימור: {finishTypes.find(f => f.id === selectedFinish)?.name}
                  </p>
                </div>
                <div className="space-y-2 text-gray-700">
                  <p><span className="font-semibold">HEX:</span> {selectedColor.hex}</p>
                  <p><span className="font-semibold">RGB:</span> {selectedColor.rgb}</p>
                </div>
                <button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-3 px-6 rounded-full font-bold transition-all duration-300 hover:scale-105 shadow-lg">
                  בחרתי את הצבע הזה
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Note */}
        <div className="mt-12 bg-amber-100 rounded-2xl p-6 max-w-3xl mx-auto">
          <p className="text-center text-amber-900">
            <span className="font-bold">שימו לב:</span> הצבעים המוצגים הם להמחשה בלבד.
            הצבע האמיתי עשוי להיראות שונה במעט בהתאם למסך המחשב שלכם.
            אנו ממליצים להתייעץ איתנו לפני בחירת הצבע הסופי.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ColorCatalog;
